/**
 * schema.js
 * 
 * Structured data (JSON-LD) builders for Touchstone tool pages.
 * Reads from the same ui_export.json data used by React components
 * so schema and UI never drift.
 */

const CANONICAL_URL = 'https://touchstone.guide';
const SITE_NAME = 'Touchstone';
const ORG_ID = `${CANONICAL_URL}#organization`;
const WEBSITE_ID = `${CANONICAL_URL}#website`;

function slugify(name) {
  return name.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '');
}

function safe(val) {
  if (val === undefined || val === null || val === '') return undefined;
  if (typeof val === 'string') return val.trim() || undefined;
  return val;
}

function ensureUrl(url) {
  if (!url) return undefined;
  return url.startsWith('http') ? url : `https://${url}`;
}

/**
 * Build SoftwareApplication + FAQ JSON-LD for a single tool.
 * Returns a complete @graph array ready to be wrapped in @context.
 */
export function buildToolSchema(tool) {
  if (!tool) return null;

  const toolUrl = `${CANONICAL_URL}/database/${slugify(tool.name)}`;
  const officialUrl = ensureUrl(tool.website);
  const dateModified = tool.enriched_at || tool.last_updated || undefined;

  const schema = {
    '@type': 'SoftwareApplication',
    '@id': `${toolUrl}#software`,
    name: tool.name,
    description: safe(tool.description),
    url: toolUrl,
    applicationCategory: safe(tool.category) || 'AI Tool',
    operatingSystem: 'Cloud',
  };

  if (officialUrl) {
    schema.sameAs = officialUrl;
  }

  if (tool.logo?.url) {
    schema.image = tool.logo.url;
  }

  // Provider
  if (tool.provider) {
    schema.provider = {
      '@type': 'Organization',
      name: tool.provider,
    };
    if (officialUrl) schema.provider.url = officialUrl;
    if (tool.founded) schema.provider.foundingDate = tool.founded;
    if (tool.headquarters) schema.provider.location = tool.headquarters;
  }

  // Pricing → offers
  if (tool.pricing) {
    const p = tool.pricing;
    schema.isAccessibleForFree = !!p.free_tier;

    const offers = [];

    if (p.free_tier) {
      offers.push({
        '@type': 'Offer',
        name: 'Free Tier',
        price: '0',
        priceCurrency: 'USD',
        availability: 'https://schema.org/InStock',
        description: 'Free tier available',
      });
    }

    if (p.api_input) {
      offers.push({
        '@type': 'Offer',
        name: 'API Access',
        description: `Input: ${p.api_input}${p.api_output ? `, Output: ${p.api_output}` : ''}`,
        priceSpecification: {
          '@type': 'UnitPriceSpecification',
          priceCurrency: 'USD',
          description: `${p.api_input} input${p.api_output ? `, ${p.api_output} output` : ''}`,
        },
      });
    }

    if (p.subscription) {
      offers.push({
        '@type': 'Offer',
        name: 'Subscription',
        description: p.subscription,
        priceCurrency: 'USD',
      });
    }

    if (offers.length > 0) {
      schema.offers = offers.length === 1 ? offers[0] : offers;
    }

    if (p.notes) {
      schema.additionalProperty = schema.additionalProperty || [];
      schema.additionalProperty.push({
        '@type': 'PropertyValue',
        propertyID: 'pricingNotes',
        name: 'Pricing Notes',
        value: p.notes,
      });
    }
  }

  // Features
  const features = tool.key_features || [];
  if (features.length > 0) {
    schema.featureList = features.join(', ');
  }

  // Best for / audience
  if (tool.best_for) {
    schema.audience = {
      '@type': 'Audience',
      audienceType: tool.best_for,
    };
  }

  // Competitors → isSimilarTo
  if (tool.competitors?.length > 0) {
    schema.isSimilarTo = tool.competitors.map(name => ({
      '@type': 'SoftwareApplication',
      name,
      url: `${CANONICAL_URL}/database/${slugify(name)}`,
    }));
  }

  // Freshness signal — last_verified overrides pipeline timestamps
  const bestDate = tool.last_verified || dateModified;
  if (bestDate) {
    schema.dateModified = typeof bestDate === 'string' && bestDate.includes('T')
      ? bestDate.split('T')[0]
      : bestDate;
  }

  // Compliance / enterprise as additionalProperty
  if (tool.enterprise) {
    schema.additionalProperty = schema.additionalProperty || [];
    Object.entries(tool.enterprise).forEach(([key, value]) => {
      if (value && value !== 'unknown' && value !== 'unclear') {
        schema.additionalProperty.push({
          '@type': 'PropertyValue',
          propertyID: key,
          name: key.replace(/_/g, ' ').toUpperCase(),
          value: String(value),
        });
      }
    });
  }

  // Scores as additionalProperty
  if (tool.scores) {
    schema.additionalProperty = schema.additionalProperty || [];
    Object.entries(tool.scores).forEach(([key, value]) => {
      if (value != null && value > 0) {
        schema.additionalProperty.push({
          '@type': 'PropertyValue',
          propertyID: `touchstone_${key}`,
          name: key.replace(/_/g, ' '),
          value: String(value),
          maxValue: '10',
          description: `Touchstone ${key.replace(/_/g, ' ')} score (0-10)`,
        });
      }
    });
  }

  // --- ENRICHED FIELDS (AEO) ---

  // Numeric pricing → proper Offer with numeric price values
  if (tool.numeric_pricing) {
    const np = tool.numeric_pricing;
    const numericOffers = [];

    if (np.input_price > 0 || np.output_price > 0) {
      numericOffers.push({
        '@type': 'Offer',
        name: `API Pricing (per ${np.unit || '1M tokens'})`,
        priceCurrency: np.currency || 'USD',
        price: String(np.input_price),
        description: np.output_price
          ? `Input: $${np.input_price}/${np.unit}, Output: $${np.output_price}/${np.unit}`
          : `$${np.input_price}/${np.unit}`,
        availability: 'https://schema.org/InStock',
      });
    }

    if (np.subscription_price > 0) {
      numericOffers.push({
        '@type': 'Offer',
        name: 'Subscription',
        priceCurrency: np.currency || 'USD',
        price: String(np.subscription_price),
        description: `$${np.subscription_price}/${np.subscription_period || 'month'}`,
        availability: 'https://schema.org/InStock',
      });
    }

    if (numericOffers.length > 0) {
      schema.offers = numericOffers.length === 1 ? numericOffers[0] : numericOffers;
    }
  }

  // Technical specs as additionalProperty
  if (tool.technical_specs) {
    schema.additionalProperty = schema.additionalProperty || [];
    const specs = tool.technical_specs;
    const specFields = ['context_window', 'max_output_tokens', 'rate_limit', 'training_cutoff',
      'resolutions', 'quality_modes', 'max_resolution', 'latest_version', 'model_sizes',
      'ide_support', 'ide_base', 'free_tier_limits'];
    specFields.forEach(key => {
      if (specs[key]) {
        schema.additionalProperty.push({
          '@type': 'PropertyValue',
          propertyID: key,
          name: key.replace(/_/g, ' '),
          value: String(specs[key]),
        });
      }
    });
    if (specs.multimodal !== undefined) {
      schema.additionalProperty.push({
        '@type': 'PropertyValue',
        propertyID: 'multimodal',
        name: 'multimodal support',
        value: specs.multimodal ? 'Yes' : 'No',
      });
    }
    if (specs.supported_languages) {
      schema.additionalProperty.push({
        '@type': 'PropertyValue',
        propertyID: 'supported_languages',
        name: 'supported languages',
        value: String(specs.supported_languages),
      });
    }
  }

  // Pricing provenance
  if (tool.pricing_source) {
    schema.additionalProperty = schema.additionalProperty || [];
    schema.additionalProperty.push({
      '@type': 'PropertyValue',
      propertyID: 'pricing_source',
      name: 'Pricing source',
      value: tool.pricing_source.url,
      description: `Verified: ${tool.pricing_source.verified_date || 'unverified'}`,
    });
  }

  // Release history as releaseNotes
  if (tool.release_history?.length > 0) {
    schema.releaseNotes = tool.release_history
      .map(r => `${r.date}: ${r.version} — ${r.summary}`)
      .join('. ');
  }

  // Compliance details as additionalProperty
  if (tool.compliance_details?.length > 0) {
    schema.additionalProperty = schema.additionalProperty || [];
    tool.compliance_details.forEach(c => {
      schema.additionalProperty.push({
        '@type': 'PropertyValue',
        propertyID: `compliance_${c.standard.toLowerCase().replace(/\s+/g, '_')}`,
        name: c.standard,
        value: c.status,
        description: c.source_url ? `Source: ${c.source_url}` : undefined,
      });
    });
  }

  // Limitations as additionalProperty
  if (tool.limitations?.length > 0) {
    schema.additionalProperty = schema.additionalProperty || [];
    tool.limitations.forEach((lim, i) => {
      schema.additionalProperty.push({
        '@type': 'PropertyValue',
        propertyID: `limitation_${i + 1}`,
        name: `Limitation (${lim.type})`,
        value: lim.limitation,
      });
    });
  }

  // Starter code as code sample
  if (tool.starter_code) {
    schema.additionalProperty = schema.additionalProperty || [];
    schema.additionalProperty.push({
      '@type': 'PropertyValue',
      propertyID: 'code_sample',
      name: `Quickstart (${tool.starter_code.language})`,
      value: tool.starter_code.code,
      description: tool.starter_code.source_url ? `Source: ${tool.starter_code.source_url}` : undefined,
    });
  }

  return schema;
}

/**
 * Build template-based FAQ for a tool from its existing data fields.
 * No LLM generation — purely mechanical Q&A from structured data.
 */
export function buildToolFAQ(tool) {
  if (!tool) return [];
  const faq = [];

  // Pricing question — enriched version uses numeric_pricing if available
  if (tool.numeric_pricing || tool.pricing) {
    const np = tool.numeric_pricing;
    const p = tool.pricing || {};
    let answer = `${tool.name} `;

    if (np) {
      if (np.free_tier) answer += 'offers a free tier. ';
      else answer += 'does not currently offer a free tier. ';
      if (np.input_price > 0) answer += `API pricing is $${np.input_price}/${np.unit} input`;
      if (np.output_price > 0) answer += ` and $${np.output_price}/${np.unit} output. `;
      else if (np.input_price > 0) answer += '. ';
      if (np.subscription_price > 0) answer += `A subscription is available at $${np.subscription_price}/${np.subscription_period || 'month'}. `;
    } else {
      if (p.free_tier) answer += 'offers a free tier. ';
      else answer += 'does not currently offer a free tier. ';
      if (p.model) answer += `The pricing model is ${p.model}. `;
      if (p.api_input) answer += `API pricing starts at ${p.api_input} for input tokens. `;
      if (p.subscription) answer += `A subscription plan is available at ${p.subscription}. `;
    }
    if (tool.pricing_source?.url) answer += `(Source: ${tool.pricing_source.url})`;
    else if (p.notes) answer += p.notes;

    faq.push({ question: `What does ${tool.name} cost?`, answer: answer.trim() });
  }

  // Best for question
  if (tool.best_for) {
    faq.push({
      question: `What is ${tool.name} best for?`,
      answer: `${tool.name} is best suited for: ${tool.best_for}.`,
    });
  }

  // Competitors question
  if (tool.competitors?.length > 0) {
    faq.push({
      question: `What are the main alternatives to ${tool.name}?`,
      answer: `The main alternatives to ${tool.name} include ${tool.competitors.join(', ')}.`,
    });
  }

  // Key features question
  if (tool.key_features?.length > 0) {
    const top = tool.key_features.slice(0, 4);
    faq.push({
      question: `What are the key features of ${tool.name}?`,
      answer: `Key features of ${tool.name} include: ${top.join('; ')}.`,
    });
  }

  // Technical specs question (enriched)
  if (tool.technical_specs?.context_window) {
    faq.push({
      question: `What is ${tool.name}'s context window?`,
      answer: `${tool.name} supports a context window of ${tool.technical_specs.context_window}.${tool.technical_specs.max_output_tokens ? ` Maximum output is ${tool.technical_specs.max_output_tokens} tokens.` : ''}`,
    });
  }

  // Compliance question (enriched)
  if (tool.compliance_details?.length > 0) {
    const certs = tool.compliance_details.map(c => `${c.standard}: ${c.status}`).join('; ');
    faq.push({
      question: `What compliance certifications does ${tool.name} have?`,
      answer: `${tool.name} compliance status: ${certs}.`,
    });
  }

  // Free tier question
  if (tool.pricing?.free_tier || tool.numeric_pricing?.free_tier) {
    const notes = tool.pricing?.notes || '';
    faq.push({
      question: `Does ${tool.name} have a free plan?`,
      answer: `Yes, ${tool.name} offers a free tier. ${notes}`.trim(),
    });
  }

  return faq.slice(0, 7);
}

/**
 * Build a Dataset JSON-LD for the /database collection page.
 */
export function buildDatasetSchema(metadata) {
  return {
    '@type': 'Dataset',
    '@id': `${CANONICAL_URL}/database#dataset`,
    name: 'Touchstone AI Tool Database',
    description: 'A neutral, curated database of AI tools with pricing, capabilities, integrations, and compliance data. Maintained by Touchstone Research.',
    url: `${CANONICAL_URL}/database`,
    creator: {
      '@type': 'Organization',
      '@id': ORG_ID,
      name: SITE_NAME,
      url: CANONICAL_URL,
    },
    dateModified: metadata?.exported
      ? metadata.exported.split('T')[0]
      : new Date().toISOString().split('T')[0],
    license: 'https://creativecommons.org/licenses/by-nc/4.0/',
    keywords: [
      'AI tools',
      'machine learning',
      'language models',
      'AI comparison',
      'software database',
    ],
    measurementTechnique: 'Independent research and documentation of real-world AI tool usage',
    variableMeasured: [
      'pricing',
      'capabilities',
      'integrations',
      'compliance',
      'documentation quality',
      'adoption metrics',
    ].map(name => ({
      '@type': 'PropertyValue',
      name,
    })),
    distribution: {
      '@type': 'DataDownload',
      encodingFormat: 'application/json',
      contentUrl: `${CANONICAL_URL}/database`,
      description: 'Browse the full database on Touchstone',
    },
  };

}

/**
 * Build a complete JSON-LD @graph for a tool detail page.
 * Combines SoftwareApplication, FAQ, Organization, and BreadcrumbList.
 */
export function buildToolPageGraph(tool) {
  if (!tool) return null;

  const toolSchema = buildToolSchema(tool);
  const faqItems = buildToolFAQ(tool);
  const toolUrl = `${CANONICAL_URL}/database/${slugify(tool.name)}`;

  const graph = [
    {
      '@type': 'Organization',
      '@id': ORG_ID,
      name: SITE_NAME,
      url: CANONICAL_URL,
      logo: `${CANONICAL_URL}/FullLogo_Transparent.png`,
    },
    {
      '@type': 'WebSite',
      '@id': WEBSITE_ID,
      name: SITE_NAME,
      url: CANONICAL_URL,
      publisher: { '@id': ORG_ID },
    },
    {
      '@type': 'BreadcrumbList',
      '@id': `${toolUrl}#breadcrumb`,
      itemListElement: [
        { '@type': 'ListItem', position: 1, name: 'Home', item: `${CANONICAL_URL}/home` },
        { '@type': 'ListItem', position: 2, name: 'Database', item: `${CANONICAL_URL}/database` },
        { '@type': 'ListItem', position: 3, name: tool.name, item: toolUrl },
      ],
    },
    {
      '@type': 'WebPage',
      '@id': `${toolUrl}#webpage`,
      name: `${tool.name} — Touchstone`,
      description: safe(tool.description) || `${tool.name} capability profile, pricing, and integrations on Touchstone.`,
      url: toolUrl,
      isPartOf: { '@id': WEBSITE_ID },
      breadcrumb: { '@id': `${toolUrl}#breadcrumb` },
      mainEntity: { '@id': `${toolUrl}#software` },
      ...(toolSchema?.dateModified ? { dateModified: toolSchema.dateModified } : {}),
    },
    toolSchema,
  ];

  if (faqItems.length > 0) {
    graph.push({
      '@type': 'FAQPage',
      mainEntity: faqItems.map(item => ({
        '@type': 'Question',
        name: item.question,
        acceptedAnswer: {
          '@type': 'Answer',
          text: item.answer,
        },
      })),
    });
  }

  return {
    '@context': 'https://schema.org',
    '@graph': graph,
  };
}

/**
 * Build a complete JSON-LD @graph for the database collection page.
 */
export function buildDatabasePageGraph(metadata, toolCount) {
  return {
    '@context': 'https://schema.org',
    '@graph': [
      {
        '@type': 'Organization',
        '@id': ORG_ID,
        name: SITE_NAME,
        url: CANONICAL_URL,
        logo: `${CANONICAL_URL}/FullLogo_Transparent.png`,
      },
      {
        '@type': 'BreadcrumbList',
        itemListElement: [
          { '@type': 'ListItem', position: 1, name: 'Home', item: `${CANONICAL_URL}/home` },
          { '@type': 'ListItem', position: 2, name: 'Database', item: `${CANONICAL_URL}/database` },
        ],
      },
      buildDatasetSchema(metadata),
      {
        '@type': 'WebPage',
        '@id': `${CANONICAL_URL}/database#webpage`,
        name: 'AI Tool Database — Touchstone',
        description: `Browse and compare ${toolCount || 98} AI tools with neutral, evidence-based capability profiles.`,
        url: `${CANONICAL_URL}/database`,
        isPartOf: { '@id': `${CANONICAL_URL}#website` },
      },
    ],
  };
}

export function buildBlogIndexNodes(posts = [], dateModified) {
  return [
    {
      '@type': 'CollectionPage',
      '@id': `${CANONICAL_URL}/blog#collection`,
      name: 'Touchstone Blog',
      url: `${CANONICAL_URL}/blog`,
      isPartOf: { '@id': WEBSITE_ID },
      about: { '@id': ORG_ID },
      mainEntity: { '@id': `${CANONICAL_URL}/blog#itemlist` },
      ...(dateModified ? { dateModified } : {}),
    },
    {
      '@type': 'ItemList',
      '@id': `${CANONICAL_URL}/blog#itemlist`,
      name: 'Touchstone blog articles',
      numberOfItems: posts.length,
      itemListElement: posts.map((post, index) => ({
        '@type': 'ListItem',
        position: index + 1,
        url: `${CANONICAL_URL}/blog/${post.slug}`,
        name: post.title,
      })),
    },
  ];
}

export function buildDatabaseCollectionNodes(tools = [], metadata = {}) {
  const dateModified = metadata?.exported
    ? metadata.exported.split('T')[0]
    : undefined;

  return [
    buildDatasetSchema(metadata),
    {
      '@type': 'CollectionPage',
      '@id': `${CANONICAL_URL}/database#collection`,
      name: 'Touchstone AI Tool Database',
      url: `${CANONICAL_URL}/database`,
      isPartOf: { '@id': WEBSITE_ID },
      about: { '@id': ORG_ID },
      mainEntity: { '@id': `${CANONICAL_URL}/database#itemlist` },
      ...(dateModified ? { dateModified } : {}),
    },
    {
      '@type': 'ItemList',
      '@id': `${CANONICAL_URL}/database#itemlist`,
      name: 'Touchstone database tool listings',
      numberOfItems: tools.length,
      itemListElement: tools.map((tool, index) => ({
        '@type': 'ListItem',
        position: index + 1,
        url: `${CANONICAL_URL}/database/${slugify(tool.name)}`,
        name: tool.name,
      })),
    },
  ];
}

export function buildContactPageNodes() {
  return [
    {
      '@type': 'ContactPage',
      '@id': `${CANONICAL_URL}/contact#webpage`,
      name: 'Contact Touchstone',
      url: `${CANONICAL_URL}/contact`,
      isPartOf: { '@id': WEBSITE_ID },
      about: { '@id': ORG_ID },
      mainEntity: { '@id': `${CANONICAL_URL}/contact#support` },
    },
    {
      '@type': 'ContactPoint',
      '@id': `${CANONICAL_URL}/contact#support`,
      contactType: 'customer support',
      email: 'hello@touchstone.dev',
      areaServed: 'Worldwide',
      availableLanguage: ['en'],
    },
  ];
}
